import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

export type UniformStatus = 'Requested' | 'Ordered' | 'Delivered' | 'Declined' | 'Cancelled'

export interface UniformItem {
  uniformItemId: number
  name: string
  sizes: string[]
  defaultAllowance: number
  unitPrice: number | null
  isActive: boolean
  sortOrder: number
  /** Limited to groups at all - can be true with no groups listed, when they've all been deleted. */
  limitedToGroups: boolean
  /** The groups it's offered to. Not limited means everyone. */
  groups: { groupId: number; name: string }[]
}

export type SaveUniformItem = Omit<UniformItem, 'uniformItemId' | 'limitedToGroups' | 'groups'>

/** A group, and the uniform items its members are offered. */
export interface UniformGroup {
  groupId: number
  name: string
  description: string | null
  groupType: string
  memberCount: number
  uniformItemIds: number[]
}

/** Where an employee stands on one item this allowance year. */
export interface UniformAllowanceSummary {
  uniformItemId: number
  name: string
  sizes: string[]
  /** The item is for everyone, or for one of their groups. */
  inItemGroups: boolean
  /** Offered to them: in the item's groups, and their own allowance isn't 0. */
  isOffered: boolean
  allowance: number
  defaultAllowance: number
  /** Their own allowance; null means they get the default. */
  allowanceOverride: number | null
  used: number
  outstanding: number
  available: number
  lastRequestedDate: string | null
  lastSize: string | null
}

export interface UniformRequestLine {
  uniformItemId: number
  itemName: string
  size: string | null
  quantity: number
  unitPrice: number | null
}

export interface UniformRequest {
  uniformRequestId: number
  employeeId: number
  employeeName: string
  requestedByEmployeeId: number | null
  requestedByName: string | null
  requestedDate: string
  status: UniformStatus
  outsideAllowance: boolean
  notes: string | null
  statusReason: string | null
  orderedDate: string | null
  deliveredDate: string | null
  statusChangedDate: string | null
  statusChangedByName: string | null
  lines: UniformRequestLine[]
  totalQuantity: number
  totalCost: number | null
}

export interface EmployeeUniform {
  employeeId: number
  employeeName: string
  yearStart: string
  yearEnd: string
  items: UniformAllowanceSummary[]
  requests: UniformRequest[]
}

/** Everyone's allowances for every active item this allowance year. */
export interface UniformAllocations {
  yearStart: string
  yearEnd: string
  items: UniformItem[]
  employees: {
    employeeId: number
    employeeName: string
    /** In the same order as items. */
    items: UniformAllowanceSummary[]
  }[]
}

export interface NewUniformRequestLine {
  uniformItemId: number
  size: string | null
  quantity: number
}

const headers = () => {
  const token = localStorage.getItem('authToken')
  if (!token) {
    throw new Error('Authorization token not found')
  }
  return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
}

const base = () => `https://${apiURL}/Uniform`

export const getUniformItems = async (includeInactive = true): Promise<UniformItem[]> =>
  (await axios.get(`${base()}/Items`, { headers: headers(), params: { includeInactive } })).data

export const saveUniformItem = async (id: number | null, item: SaveUniformItem): Promise<UniformItem> =>
  id
    ? (await axios.put(`${base()}/Items/${id}`, item, { headers: headers() })).data
    : (await axios.post(`${base()}/Items`, item, { headers: headers() })).data

export const deleteUniformItem = async (id: number): Promise<void> => {
  await axios.delete(`${base()}/Items/${id}`, { headers: headers() })
}

export const getUniformRequests = async (status?: UniformStatus, employeeId?: number): Promise<UniformRequest[]> =>
  (await axios.get(`${base()}/Requests`, { headers: headers(), params: { status, employeeId } })).data

export const updateUniformRequestStatus = async (
  id: number,
  status: UniformStatus,
  reason?: string
): Promise<UniformRequest> =>
  (await axios.put(`${base()}/Requests/${id}/Status`, { status, reason }, { headers: headers() })).data

/**
 * Raises a request for someone else. With outsideAllowance it isn't checked
 * against their allowance and never counts against it.
 */
export const createUniformRequestOnBehalf = async (request: {
  employeeId: number
  lines: NewUniformRequestLine[]
  notes: string | null
  outsideAllowance: boolean
}): Promise<{ request: UniformRequest; emailError: string | null }> =>
  (await axios.post(`${base()}/Requests/OnBehalf`, request, { headers: headers() })).data

export const getEmployeeUniform = async (employeeId: number): Promise<EmployeeUniform> =>
  (await axios.get(`${base()}/Employees/${employeeId}`, { headers: headers() })).data

/** A null quantity returns that item to its default allowance. */
export const setEmployeeUniformAllowances = async (
  employeeId: number,
  allowances: { uniformItemId: number; quantity: number | null }[]
): Promise<EmployeeUniform> =>
  (await axios.put(`${base()}/Employees/${employeeId}/Allowances`, allowances, { headers: headers() })).data

export const getUniformGroups = async (): Promise<UniformGroup[]> =>
  (await axios.get(`${base()}/Groups`, { headers: headers() })).data

/** Sets the items a group's members are offered; an item in no group is for everyone. */
export const setUniformGroupItems = async (groupId: number, uniformItemIds: number[]): Promise<UniformGroup> =>
  (await axios.put(`${base()}/Groups/${groupId}/Items`, uniformItemIds, { headers: headers() })).data

export const getUniformAllocations = async (groupId?: number): Promise<UniformAllocations> =>
  (await axios.get(`${base()}/Allocations`, { headers: headers(), params: { groupId } })).data

/** A null quantity returns that person's item to its default allowance. */
export const setUniformAllocations = async (
  allowances: { employeeId: number; uniformItemId: number; quantity: number | null }[]
): Promise<void> => {
  await axios.put(`${base()}/Allocations`, allowances, { headers: headers() })
}

/** Whether the signed-in user may manage uniform: the Uniform permission, or an administrator. */
export const canManageUniform = (permissions: { area: string; permission: string; assigned: boolean }[] | undefined | null) =>
  !!permissions?.some(
    (p) => p.assigned && ((p.area === 'Uniform' && p.permission === 'Manage') || (p.area === 'Portal' && p.permission === 'Administrator'))
  )
